import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';

interface InstitutionData {
  id: string;
  name: string;
  city: string | null;
  district: string | null;
  description: string | null;
  services: string[] | null;
  cover_image_url: string | null;
  logo_url: string | null;
  type: string | null;
  is_approved: boolean;
  is_pro_member: boolean;
}

export default function FeaturedInstitutions() {
  const { t } = useTranslation();
  const [institutions, setInstitutions] = useState<InstitutionData[]>([]);
  const [agencies, setAgencies] = useState<InstitutionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [agenciesLoading, setAgenciesLoading] = useState(true);
  const [doctorCounts, setDoctorCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    const loadInstitutions = async () => {
      try {
        const { data, error } = await supabase
          .from('institutions')
          .select('id, name, city, district, description, services, cover_image_url, logo_url, type, is_approved, is_pro_member')
          .eq('is_approved', true)
          .neq('type', 'agency')
          .order('name', { ascending: true });

        if (error) {
          console.error('Failed to load institutions:', error);
          setLoading(false);
          return;
        }

        if (data && data.length > 0) {
          setInstitutions(data);

          const ids = data.map((i) => i.id);
          const { data: doctors } = await supabase
            .from('doctors')
            .select('institution_id')
            .in('institution_id', ids);

          if (doctors) {
            const counts: Record<string, number> = {};
            doctors.forEach((d) => {
              counts[d.institution_id] = (counts[d.institution_id] || 0) + 1;
            });
            setDoctorCounts(counts);
          }
        }
      } catch (err) {
        console.error('Failed to load institutions:', err);
      }
      setLoading(false);
    };

    const loadAgencies = async () => {
      try {
        const { data, error } = await supabase
          .from('institutions')
          .select('id, name, city, district, description, services, cover_image_url, logo_url, type, is_approved, is_pro_member')
          .eq('is_approved', true)
          .eq('type', 'agency')
          .order('name', { ascending: true });

        if (!error && data) {
          setAgencies(data);
        }
      } catch (err) {
        console.error('Failed to load agencies:', err);
      }
      setAgenciesLoading(false);
    };

    loadInstitutions();
    loadAgencies();
  }, []);

  const getCategoryName = (type: string | null) => {
    const categories: Record<string, string> = {
      'ivf-merkezi': 'IVF Center',
      'kadin-dogum': 'Obstetrics & Gynecology',
      'ureme-merkezi': 'Reproductive Center',
      'genetik-merkezi': 'Genetics Center',
      'androloji': 'Andrology',
      'embriyoloji': 'Embryology Lab',
    };
    return type ? categories[type] || type : t('inst_approved_badge');
  };

  const defaultImage = 'https://public.readdy.ai/ai/img_res/b9b697ee4fc419259f62740bcae7fd73.jpg';
  const defaultAgencyImage = 'https://readdy.ai/api/search-image?query=professional%20travel%20agency%20office%20modern%20tourism%20company%20interior%20organized%20travel%20planning%20desk%20with%20maps%20and%20brochures%20bright%20clean%20workspace&width=800&height=500&seq=default-agency&orientation=landscape';

  return (
    <>
      {/* Health Institutions */}
      <section className="py-24 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 font-['Outfit']">
                {t('institutions_title')} <span className="text-rose-500">{t('institutions_title_highlight')}</span>
              </h2>
              <p className="text-base text-gray-500 mt-3 max-w-lg">
                {t('institutions_subtitle')}
              </p>
            </div>
            <Link
              to="/saglik-kurumlari"
              className="mt-6 md:mt-0 inline-flex items-center text-sm font-semibold text-rose-500 hover:text-rose-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('institutions_view_all')}
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden border border-gray-100 animate-pulse">
                  <div className="h-52 bg-gray-200"></div>
                  <div className="p-5 space-y-3">
                    <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                    <div className="h-3 bg-gray-200 rounded w-full"></div>
                    <div className="h-3 bg-gray-200 rounded w-4/5"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : institutions.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-product-shop>
              {institutions.map((institution) => (
                <Link
                  key={institution.id}
                  to={`/kurum/${institution.id}`}
                  className="group bg-white rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-500 hover:-translate-y-1 border border-gray-100"
                >
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={institution.cover_image_url || defaultImage}
                      alt={institution.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                    <div className="absolute top-4 left-4 flex items-center gap-2">
                      <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-xs font-semibold text-gray-800 rounded-full">
                        {getCategoryName(institution.type)}
                      </span>
                    </div>
                    <div className="absolute top-4 right-4">
                      <span className="flex items-center px-2.5 py-1 bg-emerald-500 text-white text-[10px] font-bold rounded-full">
                        <i className="ri-verified-badge-fill mr-1"></i>
                        {t('inst_approved_badge')}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center space-x-2 mb-2">
                      <i className="ri-map-pin-2-fill text-rose-400 text-xs"></i>
                      <span className="text-xs text-gray-500">
                        {[institution.city, institution.district].filter(Boolean).join(', ') || 'Turkey'}
                      </span>
                      {doctorCounts[institution.id] > 0 && (
                        <>
                          <span className="text-gray-300">·</span>
                          <span className="text-xs text-gray-500 flex items-center">
                            <i className="ri-user-heart-line mr-1 text-rose-400"></i>
                            {t('inst_doctor_count', { count: doctorCounts[institution.id] })}
                          </span>
                        </>
                      )}
                    </div>
                    <h3 className="text-base font-bold text-gray-900 mb-2 group-hover:text-rose-500 transition-colors font-['Outfit'] line-clamp-1">
                      {institution.name}
                    </h3>
                    <p className="text-sm text-gray-500 mb-4 line-clamp-2 leading-relaxed">
                      {institution.description}
                    </p>
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {(institution.services || []).slice(0, 3).map((service, idx) => (
                        <span key={idx} className="text-[11px] text-gray-500 bg-gray-50 px-2 py-0.5 rounded-md">
                          {service}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-end pt-4 border-t border-gray-100">
                      <span className="text-sm font-semibold text-rose-500 flex items-center whitespace-nowrap group-hover:translate-x-1 transition-transform">
                        {t('inst_explore')}
                        <i className="ri-arrow-right-s-line ml-1"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <i className="ri-hospital-line text-5xl text-gray-200 mb-3"></i>
              <p className="text-gray-400 text-sm">{t('inst_no_approved')}</p>
            </div>
          )}
        </div>
      </section>

      {/* Health Tourism Agencies */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
                <i className="ri-building-4-line"></i>
                {t('agencies_badge')}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 font-['Outfit']">
                {t('agencies_title')} <span className="text-teal-600">{t('agencies_title_highlight')}</span>
              </h2>
              <p className="text-base text-gray-500 mt-3 max-w-lg">
                {t('agencies_subtitle')}
              </p>
            </div>
            <Link
              to="/araci-kurumlar"
              className="mt-6 md:mt-0 inline-flex items-center text-sm font-semibold text-teal-600 hover:text-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('agencies_view_all')}
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>

          {/* Service badges */}
          <div className="flex flex-wrap gap-2 mb-10">
            {[
              t('service_flight'),
              t('service_hotel'),
              t('service_tour'),
              t('service_translation'),
              t('service_vip'),
            ].map((tag) => (
              <span key={tag} className="flex items-center gap-1.5 bg-teal-50 text-teal-700 text-xs font-medium px-3 py-1.5 rounded-full">
                <i className="ri-check-line"></i>
                {tag}
              </span>
            ))}
          </div>

          {agenciesLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden border border-gray-100 animate-pulse">
                  <div className="h-52 bg-gray-200"></div>
                  <div className="p-5 space-y-3">
                    <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                    <div className="h-3 bg-gray-200 rounded w-full"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : agencies.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {agencies.map((agency) => (
                <Link
                  key={agency.id}
                  to={`/kurum/${agency.id}`}
                  className="group bg-white rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-500 hover:-translate-y-1 border border-gray-100"
                >
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={agency.cover_image_url || defaultAgencyImage}
                      alt={agency.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                    {agency.logo_url && (
                      <div className="absolute top-4 left-4 bg-white p-1.5 rounded-lg">
                        <img src={agency.logo_url} alt="Logo" className="w-10 h-10 object-contain" />
                      </div>
                    )}
                    {agency.is_pro_member && (
                      <div className="absolute top-4 right-4 px-2.5 py-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-bold rounded-full flex items-center gap-1">
                        <i className="ri-vip-crown-line"></i>
                        PRO
                      </div>
                    )}
                    <div className="absolute bottom-4 left-4">
                      <span className="inline-flex items-center gap-1 bg-teal-500/90 backdrop-blur-sm text-white text-[10px] font-semibold px-2.5 py-1 rounded-full">
                        <i className="ri-building-4-line"></i>
                        {t('agencies_service_badge')}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center space-x-2 mb-2">
                      <i className="ri-map-pin-2-fill text-teal-500 text-xs"></i>
                      <span className="text-xs text-gray-500">
                        {[agency.city, agency.district].filter(Boolean).join(', ') || 'Turkey'}
                      </span>
                    </div>
                    <h3 className="text-base font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors font-['Outfit'] line-clamp-1">
                      {agency.name}
                    </h3>
                    <p className="text-sm text-gray-500 mb-4 line-clamp-2 leading-relaxed">
                      {agency.description}
                    </p>
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {(agency.services || []).slice(0, 3).map((service, idx) => (
                        <span key={idx} className="text-[11px] text-teal-700 bg-teal-50 px-2 py-0.5 rounded-md">
                          {service}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      {agency.is_pro_member && (
                        <span className="text-[11px] text-amber-600 font-medium flex items-center gap-1">
                          <i className="ri-vip-crown-line"></i>
                          {t('agencies_consult')}
                        </span>
                      )}
                      <span className="ml-auto text-sm font-semibold text-teal-600 flex items-center whitespace-nowrap group-hover:translate-x-1 transition-transform">
                        {t('agencies_explore')}
                        <i className="ri-arrow-right-s-line ml-1"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-teal-50/50 rounded-2xl border border-teal-100">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                <i className="ri-building-4-line text-3xl text-teal-400"></i>
              </div>
              <h3 className="text-base font-semibold text-gray-700 mb-1">{t('agencies_empty_title')}</h3>
              <p className="text-sm text-gray-400">{t('agencies_empty_desc')}</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
